<template>
	<div class="appointments-template-editor appointments-typography">
		<div v-if="!templateId" class="card placeholder">
			Select a template to edit.
		</div>
		<template v-else>
			<div v-if="actionError" class="inline-error">
				<p>{{ actionError }}</p>
				<button class="outline-btn" type="button" @click="clearActionError">Dismiss</button>
			</div>

			<div v-if="isLoading" class="week-rows skeleton">
				<div class="skeleton-block"></div>
			</div>
			<div v-else-if="error" class="state state-error">
				<p>{{ error }}</p>
				<button class="outline-btn" @click="loadTemplateItems">Try again</button>
			</div>
			<div v-else class="week-rows">
				<div v-for="day in weekDays" :key="day.index" class="day-row">
					<div class="day-header">
						<div class="day-label">{{ day.label }}</div>
					</div>
					<div class="appointments-container" v-if="itemsForDay(day.index).length">
						<div
							v-for="item in itemsForDay(day.index)"
							:key="item.id"
							class="appointment-row"
							:class="{ 'is-pending': item.__pending }"
						>
							<div class="appointment-details">
								<div class="dynamic-fields">
									<div class="field-input-container field-building">
										<CatalogSearchInput use-fixed
											:modelValue="editingValues[item.id].buildingId"
											@update:modelValue="(val) => updateSelect(item, 'buildingId', val)"
											:options="buildingOptions"
											placeholder="Building"
											v-tooltip
										/>
									</div>
									<div class="field-input-container field-supplier">
										<CatalogSearchInput use-fixed
											:modelValue="editingValues[item.id].supplierId"
											@update:modelValue="(val) => updateSelect(item, 'supplierId', val)"
											:options="supplierOptions"
											placeholder="Supplier"
											v-tooltip
										/>
									</div>
									<div class="field-input-container field-service">
										<CatalogSearchInput use-fixed
											:modelValue="editingValues[item.id].serviceId"
											@update:modelValue="(val) => updateSelect(item, 'serviceId', val)"
											:options="serviceOptions"
											placeholder="Service"
											v-tooltip
										/>
									</div>
									<div class="field-input-container field-flat">
										<input
											type="text"
											v-model="editingValues[item.id].flatRef"
											@input="requestSave(item)"
											class="field-input field-text"
											placeholder="Flat ref"
										/>
									</div>
									<div class="field-input-container field-hours">
										<input
											type="number" inputmode="decimal"
											min="0"
											step="0.25"
											v-model="editingValues[item.id].hours"
											@input="requestSave(item)"
											class="field-input field-number"
											placeholder="Hours"
										/>
									</div>
								</div>
							</div>
							<div class="appointment-actions">
								<div class="action-wrapper">
									<button
										@click.stop="toggleSubmenu(item.id)"
										title="More options" aria-label="More options"
										class="more-options-btn"
									>
										<AppointmentIcon name="more" class="icon-more" />
									</button>
									<div v-if="openSubmenuId === item.id" class="submenu" v-floating-menu>
										<button
											@click="handleDelete(item)"
											class="submenu-btn delete"
											:disabled="isDeleting(item.id)"
										>
											Delete template item
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="day-footer">
						<button class="noc-back-btn secondary" @click="handleAdd(day.index)" :disabled="isAdding(day.index)">
							<AppointmentIcon name="plus" class="btn-icon" /> Add template appointment
						</button>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup>
import vFloatingMenu from '@/directives/floatingMenu';
import { computed, getCurrentInstance, onBeforeUnmount, onDeactivated, onMounted, reactive, ref, watch } from 'vue';
import { useCatalogStore } from '@/stores/catalog';
import { useJournalAppointmentTemplatesStore } from '@/stores/journalAppointmentTemplates';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';

const props = defineProps({
	template: {
		type: Object,
		default: null,
	},
});

const DAY_LABELS = [
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
	'Sunday',
];

const store = useJournalAppointmentTemplatesStore();
const catalogStore = useCatalogStore();
const instance = getCurrentInstance();
const logError = instance?.proxy?.$logError;
const toast = instance?.proxy?.$toast;

const templateId = computed(() => props.template?.id || null);
const isLoading = computed(() => store.isLoadingItems);
const error = computed(() => store.itemsError);
const buildingOptions = computed(() => store.buildingOptions);
const supplierOptions = computed(() => store.supplierOptions);
const serviceOptions = computed(() => store.serviceOptions);
const weekDays = computed(() => DAY_LABELS.map((label, index) => ({ label, index })));

const editingValues = ref({});
const deletingState = ref({});
const addingState = ref({});
const savingState = reactive({});
const saveTimers = reactive({});
const queuedSaves = reactive({});
const openSubmenuId = ref(null);
const actionError = ref('');

const itemsByDay = computed(() => {
	const grouped = {};
	(store.items || []).forEach((item) => {
		const key = Number.isFinite(Number(item.dayIndex)) ? Number(item.dayIndex) : null;
		if (key === null) return;
		if (!grouped[key]) grouped[key] = [];
		grouped[key].push(item);
	});
	return grouped;
});

const itemsForDay = (dayIndex) => itemsByDay.value[dayIndex] || [];

const clearActionError = () => {
	actionError.value = '';
};

const setActionError = (message) => {
	actionError.value = message || 'Something went wrong. Please try again.';
};

const getErrorMessage = (err, fallback) =>
	err?.response?.data?.error?.message || err?.message || fallback;

const loadTemplateItems = async () => {
	if (!templateId.value) {
		await store.fetchTemplateItems(null);
		editingValues.value = {};
		return;
	}
	try {
		const items = await store.fetchTemplateItems(templateId.value);
		seedEditingValues(items);
		clearActionError();
	} catch (err) {
		logError(err, 'AppointmentsTemplateEditor loadTemplateItems');
	}
};

const seedEditingValues = (items = []) => {
	const map = {};
	items.forEach((item) => {
		map[item.id] = {
			buildingId: item.building?.id || '',
			supplierId: item.supplier?.id || '',
			serviceId: item.service?.id || '',
			flatRef: item.flatRef || '',
			hours: item.hours ?? '',
		};
	});
	editingValues.value = map;
};

const ensureEditingEntries = (items = []) => {
	const next = { ...editingValues.value };
	let changed = false;
	items.forEach((item) => {
		if (!item?.id || next[item.id]) return;
		next[item.id] = {
			buildingId: item.building?.id || '',
			supplierId: item.supplier?.id || '',
			serviceId: item.service?.id || '',
			flatRef: item.flatRef || '',
			hours: item.hours ?? '',
		};
		changed = true;
	});
	if (changed) {
		editingValues.value = next;
	}
};

const findItemById = (id) => {
	if (!id) return null;
	return store.items.find((item) => item.id === id) || null;
};

const requestSave = (item, { debounce = true } = {}) => {
	const target = findItemById(item?.id) || item;
	if (!target?.id) {
		return;
	}
	if (debounce) {
		if (saveTimers[target.id]) {
			clearTimeout(saveTimers[target.id]);
		}
		saveTimers[target.id] = setTimeout(() => {
			saveTimers[target.id] = null;
			saveTemplateItem(target);
		}, 350);
	} else {
		if (saveTimers[target.id]) {
			clearTimeout(saveTimers[target.id]);
			saveTimers[target.id] = null;
		}
		saveTemplateItem(target);
	}
};

const syncUpdatedItem = (updated) => {
	if (!updated?.id) {
		return false;
	}
	const items = store.items || [];
	const index = items.findIndex((item) => item.id === updated.id);
	if (index === -1) {
		return false;
	}
	const next = [...items];
	next[index] = { ...items[index], ...updated };
	store.$patch({ items: next });
	editingValues.value = {
		...editingValues.value,
		[updated.id]: {
			buildingId: updated.building?.id || '',
			supplierId: updated.supplier?.id || '',
			serviceId: updated.service?.id || '',
			flatRef: updated.flatRef || '',
			hours: updated.hours ?? '',
		},
	};
	return true;
};

const removeItemLocally = (item) => {
	if (!item?.id) {
		return null;
	}
	const items = store.items || [];
	const index = items.findIndex((entry) => entry.id === item.id);
	if (index === -1) {
		return null;
	}
	const removedEntry = items[index];
	const next = [...items];
	next.splice(index, 1);
	store.$patch({ items: next });
	const editingSnapshot = editingValues.value[item.id]
		? { ...editingValues.value[item.id] }
		: null;
	const nextEditing = { ...editingValues.value };
	delete nextEditing[item.id];
	editingValues.value = nextEditing;
	return {
		index,
		entry: removedEntry,
		editing: editingSnapshot,
	};
};

const restoreItemSnapshot = (snapshot) => {
	if (!snapshot?.entry || snapshot.index === undefined) {
		return false;
	}
	const items = store.items || [];
	const next = [...items];
	const insertIndex = Math.min(snapshot.index, next.length);
	next.splice(insertIndex, 0, snapshot.entry);
	store.$patch({ items: next });
	if (snapshot.entry?.id && snapshot.editing) {
		editingValues.value = {
			...editingValues.value,
			[snapshot.entry.id]: snapshot.editing,
		};
	}
	return true;
};

const createPendingItem = (dayIndex) => {
	const tempId = `pending-${dayIndex}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const placeholder = {
		id: tempId,
		documentId: null,
		dayIndex,
		__pending: true,
		building: null,
		supplier: null,
		service: null,
		flatRef: '',
		hours: null,
	};
	store.$patch({ items: [...(store.items || []), placeholder] });
	editingValues.value = {
		...editingValues.value,
		[tempId]: {
			buildingId: '',
			supplierId: '',
			serviceId: '',
			flatRef: '',
			hours: '',
		},
	};
	return placeholder;
};

const replacePendingItem = (tempId, created) => {
	if (!created?.id) {
		return false;
	}
	const items = store.items || [];
	const index = items.findIndex((entry) => entry.id === tempId);
	if (index === -1) {
		return false;
	}
	const { __pending: _ignored, ...fallbackEntry } = items[index] || {};
	const mergedBuilding = created.building || fallbackEntry.building || null;
	const mergedSupplier = created.supplier || fallbackEntry.supplier || null;
	const mergedService = created.service || fallbackEntry.service || null;
	const mergedFlatRef = created.flatRef ?? fallbackEntry.flatRef ?? '';
	const nextEntries = [...items];
	nextEntries[index] = {
		...fallbackEntry,
		...created,
		__pending: false,
		building: mergedBuilding,
		supplier: mergedSupplier,
		service: mergedService,
		flatRef: mergedFlatRef,
	};
	store.$patch({ items: nextEntries });
	const { [tempId]: _, ...rest } = editingValues.value;
	editingValues.value = {
		...rest,
		[created.id]: {
			buildingId: mergedBuilding?.id || '',
			supplierId: mergedSupplier?.id || '',
			serviceId: mergedService?.id || '',
			flatRef: mergedFlatRef || '',
			hours: created.hours ?? '',
		},
	};
	return true;
};

const handleClickOutside = (event) => {
	if (!event.target.closest('.action-wrapper')) {
		openSubmenuId.value = null;
	}
};

onMounted(async () => {
	document.addEventListener('click', handleClickOutside, true);
	try {
		if (!catalogStore.initialized) {
			await catalogStore.bootstrap?.();
		}
	} catch (err) {
		logError(err, 'AppointmentsTemplateEditor bootstrapCatalog');
	}
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside, true);
	Object.values(saveTimers).forEach((timer) => {
		if (timer) {
			clearTimeout(timer);
		}
	});
});

watch(
	templateId,
	async () => {
		await loadTemplateItems();
	},
	{ immediate: true },
);

watch(
	() => store.items,
	(items) => {
		if (Array.isArray(items)) {
			ensureEditingEntries(items);
		}
	},
	{ deep: false, immediate: true },
);

const handleAdd = async (dayIndex) => {
	if (!templateId.value) return;
	if (addingState.value[dayIndex]) {
		toast?.info?.('Already creating a template item for this day. Please wait.');
		return;
	}
	addingState.value[dayIndex] = true;
	const placeholder = createPendingItem(dayIndex);
	try {
		const created = await store.createTemplateItem({
			templateId: templateId.value,
			dayIndex,
		});
		const replaced = replacePendingItem(placeholder.id, created);
		if (!replaced) {
			await loadTemplateItems();
		}
		clearActionError();
	} catch (err) {
		removeItemLocally(placeholder);
		logError(err, 'AppointmentsTemplateEditor handleAdd');
		setActionError(getErrorMessage(err, 'Failed to create template item.'));
	} finally {
		addingState.value[dayIndex] = false;
	}
};

const saveTemplateItem = async (item) => {
	const current = findItemById(item?.id) || item;
	const entry = current?.id ? editingValues.value[current.id] : null;
	if (!entry || !current?.documentId) return;
	if (savingState[current.id]) {
		queuedSaves[current.id] = true;
		return;
	}
	savingState[current.id] = true;
	try {
		const hoursFieldBlank =
			entry.hours === '' || entry.hours === null || entry.hours === undefined;
		const parsedHours = hoursFieldBlank ? null : Number(entry.hours);
		if (!hoursFieldBlank && Number.isNaN(parsedHours)) {
			setActionError('Enter a valid hours value.');
			return;
		}
		if (parsedHours !== null && parsedHours < 0) {
			setActionError('Hours cannot be negative.');
			return;
		}
		const updated = await store.updateTemplateItem(current.documentId, {
			building: entry.buildingId || null,
			supplier: entry.supplierId || null,
			service: entry.serviceId || null,
			flatRef: entry.flatRef || '',
			hours: parsedHours,
		});
		const synced = syncUpdatedItem(updated);
		if (!synced) {
			await loadTemplateItems();
		}
		clearActionError();
	} catch (err) {
		logError(err, 'AppointmentsTemplateEditor saveTemplateItem');
		setActionError(getErrorMessage(err, 'Failed to update template item.'));
	} finally {
		delete savingState[current.id];
		if (queuedSaves[current.id]) {
			queuedSaves[current.id] = false;
			saveTemplateItem(findItemById(current.id) || current);
		}
	}
};

const updateSelect = (item, field, value) => {
	const entry = editingValues.value[item.id];
	if (!entry) return;
	const normalizedValue = value ?? '';
	if (entry[field] === normalizedValue) {
		return;
	}
	entry[field] = normalizedValue;
	requestSave(item, { debounce: false });
};

const toggleSubmenu = (itemId) => {
	if (openSubmenuId.value === itemId) {
		openSubmenuId.value = null;
	} else {
		openSubmenuId.value = itemId;
	}
};

const handleDelete = async (item) => {
	openSubmenuId.value = null;
	const documentId = item.documentId;
	const entryId = item.id;
	if (!documentId || !entryId) return;
	const snapshot = removeItemLocally(item);
	deletingState.value[entryId] = true;
	try {
		await store.deleteTemplateItem(documentId);
		if (!snapshot) {
			await loadTemplateItems();
		}
		clearActionError();
	} catch (err) {
		const restored = restoreItemSnapshot(snapshot);
		if (!restored) {
			await loadTemplateItems();
		}
		logError(err, 'AppointmentsTemplateEditor handleDelete');
		setActionError(getErrorMessage(err, 'Failed to delete template item.'));
	} finally {
		deletingState.value[entryId] = false;
	}
};

const isDeleting = (id) => !!deletingState.value[id];
const isAdding = (dayIndex) => !!addingState.value[dayIndex];
onDeactivated(() => {
	openSubmenuId.value = null;
});
</script>

<style scoped>
button,
.noc-back-btn {
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	font: var(--ap-type-control);
}

.appointments-template-editor {
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
}

.card.placeholder {
	border: 1px dashed var(--border-color, #d6d6d6);
	border-radius: 8px;
	padding: 32px;
	text-align: center;
	color: var(--text-color-secondary);
	font: var(--ap-type-body);
}

.inline-error {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	border: 1px solid var(--noc-red, #c45151);
	border-radius: 6px;
	padding: 12px 16px;
	background-color: var(--noc-red-05, rgba(196, 81, 81, 0.05));
	color: var(--noc-red, #c45151);
}

.inline-error p {
	margin: 0;
	flex: 1;
	font: var(--ap-type-body);
}

.week-rows {
	display: flex;
	flex-direction: column;
	gap: 0;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	box-shadow: none;
	padding: 0;
	background: var(--border-color, #dcdcdc);
	overflow: hidden;
}

.week-rows.skeleton {
	background: none;
	border: none;
	box-shadow: none;
}

.week-rows.skeleton .skeleton-block {
	width: 100%;
	height: 600px;
	border-radius: 8px;
	background: linear-gradient(
		90deg,
		var(--noc-background-secondary, #f5f5f5) 25%,
		var(--noc-background, #fff) 37%,
		var(--noc-background-secondary, #f5f5f5) 63%
	);
	background-size: 400% 100%;
	animation: skeleton-loading 1.2s ease-in-out infinite;
}

@keyframes skeleton-loading {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

.day-row {
	background: var(--noc-background, #fff);
	padding: 16px 0;
	display: flex;
	flex-direction: column;
	gap: 0;
}

.week-rows .day-row:first-child {
	padding-top: 0;
}

.day-header {
	display: flex;
	flex-direction: row;
	gap: 12px;
	align-items: center;
	padding: 0 12px 0 16px;
	justify-content: flex-start;
	height: 40px;
	min-height: 40px;
}

.day-footer {
	display: flex;
	padding: 0 16px;
	justify-content: flex-start;
	margin-top: 8px;
}

.day-footer .noc-back-btn.secondary {
	border-radius: 8px;
	box-shadow: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	border: 1px solid var(--border-color);
	height: 34px;
	padding: 0 12px;
	font: var(--ap-type-control);
}

.day-footer .noc-back-btn.secondary:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
	filter: none;
}

.day-label {
	text-align: left;
	font: var(--ap-type-section);
}

.appointments-container {
	display: flex;
	flex-direction: column;
	gap: 0;
	border-top: 1px solid var(--border-color, #dcdcdc);
	border-bottom: 1px solid var(--border-color, #dcdcdc);
}

.appointment-row {
	display: flex;
	align-items: center;
	width: 100%;
	padding: 0;
	background-color: var(--noc-background, #fff);
	gap: 0;
	border: none;
	border-radius: 0;
}

.appointment-row:not(:last-child) {
	border-bottom: 1px solid var(--border-color, #dcdcdc);
}

.appointments-container .appointment-row:first-child {
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}

.appointments-container .appointment-row:last-child {
	border-bottom-left-radius: 4px;
	border-bottom-right-radius: 4px;
}

.appointments-container .appointment-row:first-child .field-input-container:first-child,
.appointments-container .appointment-row:first-child .field-input-container:first-child :deep(.field-input) {
	border-top-left-radius: 4px;
}

.appointments-container .appointment-row:first-child .field-input-container:last-child,
.appointments-container .appointment-row:first-child .field-input-container:last-child :deep(.field-input) {
	border-top-right-radius: 4px;
}

.appointments-container .appointment-row:last-child .field-input-container:first-child,
.appointments-container .appointment-row:last-child .field-input-container:first-child :deep(.field-input) {
	border-bottom-left-radius: 4px;
}

.appointments-container .appointment-row:last-child .field-input-container:last-child,
.appointments-container .appointment-row:last-child .field-input-container:last-child :deep(.field-input) {
	border-bottom-right-radius: 4px;
}

.appointment-row.is-pending {
	pointer-events: none;
}

.appointment-details {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0;
}

.dynamic-fields {
	width: 100%;
	display: grid;
	grid-template-columns: repeat(3, minmax(140px, 2fr)) repeat(2, minmax(100px, 1fr));
	gap: 0;
	align-items: stretch;
}

.field-input-container {
	display: flex;
	min-width: 0;
}

.field-input-container:not(:first-child) {
	border-left: 1px solid var(--border-color, #dcdcdc);
}

.field-input-container :deep(.catalog-search-input) {
	width: 100%;
}

.field-input-container :deep(.catalog-search-input .field-input),
.field-input {
	min-height: 36px;
	padding: 8px 16px;
	border: none;
	background-color: var(--noc-background, #fff);
	color: var(--text-color, #000);
	outline: none;
	transition: background-color 0.1s ease;
	width: 100%;
	font: var(--ap-type-body);
}

.field-input-container :deep(.catalog-search-input .field-input):hover,
.field-input:hover {
	background-color: var(--noc-background-secondary, #fafafa);
	cursor: text;
}

.field-input::placeholder {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.6;
}

.field-number {
	text-align: left;
	font-variant-numeric: tabular-nums;
}

.appointment-actions {
	display: flex;
	gap: 0;
	align-items: center;
	padding-right: 12px;
	position: relative;
}

.action-wrapper {
	position: relative;
}

.more-options-btn {
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	font-size: 1.1rem;
	padding: 4px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	line-height: 1;
}

.more-options-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
}


.submenu {
	position: absolute;
	right: 0;
	top: 100%;
	background-color: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	z-index: 10;
	min-width: 170px;
	padding: 4px 0;
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 8px 16px;
	background: none;
	border: none;
	color: var(--text-color, #000);
	text-align: left;
	cursor: pointer;
	white-space: nowrap;
	font: var(--ap-type-control);
}

.submenu-btn.delete {
	color: var(--noc-red, #c45151);
}

.submenu-btn.delete:hover:not(:disabled) {
	background-color: var(--noc-red-10, rgba(196, 81, 81, 0.1));
	color: var(--noc-red, #c45151);
}


.primary-btn,
.outline-btn,
.text-btn,
select,
input {
	font: var(--ap-type-body);
}

.outline-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.outline-btn {
	border: 1px solid var(--border-color, #ccc);
	background: transparent;
	border-radius: 4px;
	padding: 8px 20px;
	cursor: pointer;
}

.state {
	padding: 24px;
	text-align: center;
	color: var(--text-color-secondary);
	font: var(--ap-type-body);
}

.state-error {
	color: var(--noc-red, #c45151);
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: center;
}
.primary-btn,
.outline-btn,
.text-btn {
	font: var(--ap-type-control);
}

.catalog-search-input :deep(.dropdown-menu li),
.catalog-search-input :deep(.dropdown-menu.no-results) {
	font: var(--ap-type-body);
}

.catalog-search-input :deep(.dropdown-menu li.is-selected) {
	font-weight: 500;
}
</style>
